// apps/web/src/components/visitors/VisitorCard/modes/FullMode.tsx
import React from 'react';
import { Box, Typography, Divider, useTheme, IconButton, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Badge as BadgeIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Visitor, ActiveSubscriptionInfo } from '@gym-management/shared';
import VisitorAvatar from '../VisitorAvatar';
import VisitorStatusChips from '../VisitorStatusChips';
import VisitorActions from '../VisitorActions';

interface FullModeProps {
  visitor: Visitor;
  isCheckedIn: boolean;
  hasActiveSubscription?: boolean;
  activeSubscription?: ActiveSubscriptionInfo;
  onEdit?: (visitor: Visitor) => void;
  onDelete?: (id: string) => void;
  onViewDetails?: (visitor: Visitor) => void;
  onShowQR?: (visitor: Visitor) => void;
  onShowHistory?: (visitor: Visitor) => void;
  onCheckIn?: () => void;
  onCheckOut?: () => void;
}

const FullMode: React.FC<FullModeProps> = ({
  visitor,
  isCheckedIn,
  hasActiveSubscription,
  activeSubscription,
  onEdit,
  onDelete,
  onViewDetails,
  onShowQR,
  onShowHistory,
  onCheckIn,
  onCheckOut,
}) => {
  const theme = useTheme();
  const formattedDate = format(new Date(visitor.createdAt), 'dd MMM yyyy', { locale: ru });

  const handleCopyId = () => {
    navigator.clipboard.writeText(visitor.id);
    // можно добавить уведомление, но пока без
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Блок статусов (включает чипы, дату окончания, прогресс) */}
      <Box sx={{ mb: 3 }}>
        <VisitorStatusChips
          hasActiveSubscription={hasActiveSubscription}
          isActive={visitor.active}
          isCheckedIn={isCheckedIn}
          variant="full"
          subscriptionName={activeSubscription?.subscriptionTypeName}
          remaining={activeSubscription?.subscriptionTypeRemaining}
          subscriptionType={activeSubscription?.subscriptionTypeType}
          totalCount={activeSubscription?.subscriptionTypeCount}
          endDate={activeSubscription?.endDate}
        />
      </Box>

      {/* Аватар и имя */}
      <Box display="flex" alignItems="center" gap={3} mb={3}>
        <VisitorAvatar
          photoUrl={visitor.photoUrl}
          name={visitor.name}
          isActive={visitor.active}
          size="large"
        />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {visitor.name}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            <PhoneIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
            {visitor.phone}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Основная информация (ID и дата регистрации) */}
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.background.default, 0.5),
          border: `1px solid ${theme.palette.divider}`,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Основная информация
        </Typography>

        {/* ID с возможностью копирования */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <BadgeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                ID
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                }}
              >
                {visitor.id}
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Копировать ID">
            <IconButton size="small" onClick={handleCopyId}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Дата регистрации */}
        <Box display="flex" alignItems="center" gap={1.5}>
          <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Регистрация
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {formattedDate}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Кнопки действий */}
      <VisitorActions
        visitor={visitor}
        onEdit={onEdit}
        onDelete={onDelete}
        onViewDetails={onViewDetails}
        onShowQR={onShowQR}
        onShowHistory={onShowHistory}
        onCheckIn={onCheckIn}
        onCheckOut={onCheckOut}
        isCheckedIn={isCheckedIn}
        variant="full"
      />
    </Box>
  );
};

export default FullMode;
